// react
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { SpendPlan } from '../../../../@types';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// services
import { deleteSpendPlan, fetchSpendPlans } from '../../../../services/api';

// mui
import { alpha, Avatar, Box, Paper, Stack, Tooltip, Typography } from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	Add as IconAdd,
	CalendarMonth as IconCalendar,
	Download as IconDownload,
	Payments as IconSpendPlan,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// components

// data
import { COLORS } from '../../../../data';

// design system

// v2 components
import { CustomFieldsLink } from '../../../ui/CustomFieldsLink/CustomFieldsLink';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { StatCardRow } from '../../../ui/StatCardRow/StatCardRow';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';

// dialogs
import {
	DialogBulkUpload,
	v2SpendPlanSchema,
	downloadTemplate,
	schemaWithCustomFields,
	LookupData,
} from '../../../dialogs/DialogBulkUpload';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { getProjectLocalCurrency } from '../../../../utils/localCurrency';
import { resolveSpendPlanFxRate } from '../../../../utils/fxRate';

// local
import { SpendPlansDataTable } from './SpendPlansDataTable';
import { DialogActions } from '../../../dialogs/DialogActions/DialogActions';

// hooks
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

// Component

export const SpendPlanning = () => {
	// hooks
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	// `workPlans` (full objects with budget/FX data) backs the local-currency FX
	// resolution in summaryStats; the bulk-upload picker uses `lookups.workPlans`.
	const { accounts, refetchAccounts, workPlans } = useContext(ContextSpendPlan);

	// params
	const paramDialog = searchParams.get('dialog');
	const filterStatus = searchParams.get('status') || 'ALL';
	const filterAccount = searchParams.get('account');
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'created';
	const filterOrder = (searchParams.get('order') || 'DESC') as 'ASC' | 'DESC';
	const searchQuery = searchParams.get('search') || '';

	// state
	const [spendPlans, setSpendPlans] = useState<SpendPlan[]>([]);
	const [pagination, setPagination] = useState({ page: 1, perPage: 10, total: 0, totalPages: 0 });
	const [isLoading, setIsLoading] = useState(false);
	const [spendPlanPendingHardDelete, setSpendPlanPendingHardDelete] = useState<SpendPlan | null>(
		null
	);
	const [isHardDeletingSpendPlan, setIsHardDeletingSpendPlan] = useState(false);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// Fetch spend plans from API
	useEffect(() => {
		const loadSpendPlans = async () => {
			if (!project?.id || !accessToken || !idToken) return;

			setIsLoading(true);
			try {
				const result = await fetchSpendPlans(
					{ accessToken, idToken },
					{
						project: project.id,
						limit: Number(filterLimit),
						page: Number(filterPage),
						orderBy: filterOrderBy,
						order: filterOrder,
						search: searchQuery || undefined,
						status: filterStatus !== 'ALL' ? filterStatus : undefined,
						account: filterAccount ? Number(filterAccount) : undefined,
						populate: 'account',
					}
				);

				if (result.error) {
					setBanner({
						message: result.error.message || 'Failed to load spend plans',
						severity: 'error',
					});
					return;
				}

				setSpendPlans(result.data || []);
				if (result.pagination) {
					setPagination(result.pagination);
				}
			} catch (error) {
				setBanner({
					message: 'Failed to load spend plans',
					severity: 'error',
				});
			} finally {
				setIsLoading(false);
			}
		};

		loadSpendPlans();
	}, [
		project?.id,
		accessToken,
		idToken,
		filterLimit,
		filterPage,
		filterOrderBy,
		filterOrder,
		searchQuery,
		filterStatus,
		filterAccount,
		setBanner,
	]);

	// handlers - pagination and sorting
	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (orderBy: string, order: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', orderBy);
			params.set('order', order);
			return params;
		});
	};

	// handlers - filters
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			params.set('page', '1');
			return params;
		});
	};

	const handleFilterChange = (key: string, value: string | number) => {
		setSearchParams((params) => {
			if (key === 'status') {
				if (value === 'ALL') {
					params.delete('status');
				} else {
					params.set('status', String(value));
				}
			} else if (key === 'account') {
				if (value === 'ALL') {
					params.delete('account');
				} else {
					params.set('account', String(value));
				}
			}
			params.set('page', '1');
			return params;
		});
	};

	// handlers - navigation
	const handleOpenCreateDialog = () => {
		navigate(`/project/${project?.idWbg}/spend-plans/spend-planning/create`);
	};

	const handleOpenEditDialog = (spendPlan: SpendPlan) => {
		navigate(`/project/${project?.idWbg}/spend-plans/spend-planning/edit/${spendPlan.id}`);
	};

	const handleDeleteSpendPlan = (spendPlan: SpendPlan) => {
		setSpendPlanPendingHardDelete(spendPlan);
	};

	const handleConfirmHardDeleteSpendPlan = async () => {
		if (!spendPlanPendingHardDelete || !accessToken || !idToken) return;
		setIsHardDeletingSpendPlan(true);
		const response = await deleteSpendPlan({ accessToken, idToken }, spendPlanPendingHardDelete.id);
		setIsHardDeletingSpendPlan(false);

		if (!response.error) {
			setBanner({
				message: `Spend plan "${spendPlanPendingHardDelete.name}" deleted.`,
				severity: 'success',
			});
			setSpendPlans((prev) => prev.filter((sp) => sp.id !== spendPlanPendingHardDelete.id));
		} else {
			const hint = (response.error as { details?: { hint?: string } }).details?.hint;
			setBanner({
				message: `Failed to delete spend plan: ${response.error.message}${hint ? ` ${hint}` : ''}`,
				severity: 'error',
			});
		}

		setSpendPlanPendingHardDelete(null);
	};

	const handleViewSpendPlan = (spendPlan: SpendPlan) => {
		// Navigate to detail page
		navigate(`/project/${project?.idWbg}/spend-plans/spend-planning/${spendPlan.id}`);
	};

	// Data is already filtered by the API, use directly
	const filteredSpendPlans = spendPlans;

	// Resolve the currency that pertains to a spend plan's designated account.
	// Account currency is authoritative (balance currency → account currency),
	// falling back to the spend plan's own currency only if the account is
	// not yet loaded in context.
	const getSpendPlanCurrency = useMemo(() => {
		return (spendPlan: SpendPlan): string => {
			const accountId =
				typeof spendPlan.account === 'number' ? spendPlan.account : spendPlan.account?.id;
			const account = accounts.find((a) => a.id === accountId);
			return account?.balances?.[0]?.currency || account?.currency || spendPlan.currency || 'USD';
		};
	}, [accounts]);

	// Lookup data for bulk upload
	// Work plans for bulk-upload relation resolution, fetched as the full
	// project universe (no page limit) so the picker lists every work plan, not
	// just the current listing page. Accounts are already fully loaded in context.
	const lookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: paramDialog === 'bulkUpload',
		workPlans: true,
	});
	const lookupData: LookupData = useMemo(
		() => ({
			accounts: accounts.map((a) => ({ id: a.id, name: a.name })),
			workPlans: lookups.workPlans,
		}),
		[accounts, lookups.workPlans]
	);

	const handleBulkUploadFile = () => {
		setSearchParams((params) => {
			params.set('dialog', 'bulkUpload');
			return params;
		});
	};

	const handleDownloadTemplate = async () => {
		try {
			await downloadTemplate({
				schema: schemaWithCustomFields(v2SpendPlanSchema, project?.customFieldDefinitions ?? []),
				lookupData,
			});
			setBanner({ message: 'Template downloaded', severity: 'success' });
		} catch {
			setBanner({ message: 'Failed to generate template', severity: 'error' });
		}
	};

	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			return params;
		});
	};

	// summary stats
	const summaryStats = useMemo(() => {
		// Planned amounts are grouped by the designated account's currency. Amounts
		// in different currencies cannot be summed into a single figure without FX
		// rates, so each currency is totalled separately.
		const plannedByCurrency = new Map<string, number>();
		// Earmarked funds reserved against bank accounts. A spend plan's earmark
		// equals its planned worksheet total (see FundingEarmarks), so this is
		// grouped by account currency the same way as the planned figure.
		const earmarkedByCurrency = new Map<string, number>();
		// Local-currency total: every plan restated onto its work plan's budget
		// rate and summed into the single project local currency. If any plan
		// cannot be resolved (no local budget), the total is suppressed rather
		// than under-reported.
		const localCurrency = getProjectLocalCurrency(project);
		let totalPlannedLocal = 0;
		let localResolvable = !!localCurrency;
		spendPlans.forEach((sp) => {
			const currency = getSpendPlanCurrency(sp);
			plannedByCurrency.set(
				currency,
				(plannedByCurrency.get(currency) || 0) + (sp.totalPlanned || 0)
			);
			earmarkedByCurrency.set(
				currency,
				(earmarkedByCurrency.get(currency) || 0) + (sp.totalPlanned || 0)
			);

			if (!localCurrency) return;
			const value = sp.totalPlanned || 0;
			if (currency === localCurrency) {
				totalPlannedLocal += value;
				return;
			}
			const workPlanId = typeof sp.workPlan === 'number' ? sp.workPlan : sp.workPlan?.id;
			const workPlan = workPlans.find((wp) => wp.id === workPlanId);
			const fx = resolveSpendPlanFxRate(sp, workPlan, project);
			if (!fx) {
				localResolvable = false;
				return;
			}
			totalPlannedLocal += value * fx.rate;
		});
		return {
			approvedCount: spendPlans.filter((sp) => sp.status === 'APPROVED').length,
			completedCount: spendPlans.filter((sp) => sp.status === 'COMPLETED').length,
			draftCount: spendPlans.filter((sp) => sp.status === 'DRAFT').length,
			totalCount: spendPlans.length,
			plannedByCurrency,
			earmarkedByCurrency,
			plannedLocal:
				localResolvable && localCurrency
					? formatCurrency({ currency: localCurrency, value: totalPlannedLocal })
					: undefined,
		};
	}, [spendPlans, getSpendPlanCurrency, project, workPlans]);

	// Render a currency-grouped total. A single currency renders normally; when
	// multiple currencies are present they cannot be merged into one number, so a
	// compact "per currency" summary is shown with an asterisk and a tooltip that
	// breaks out the full amount for each currency.
	const renderCurrencyTotal = (totals: Map<string, number>): ReactNode => {
		const entries = [...totals.entries()];
		if (entries.length === 0) return formatCurrency({ value: 0, currency: 'USD' });
		if (entries.length === 1) {
			const [currency, value] = entries[0];
			return formatCurrency({ value, currency });
		}
		const breakdown = entries
			.map(([currency, value]) => formatCurrency({ value, currency }))
			.join('  +  ');
		const compact = entries
			.map(([currency, value]) =>
				formatCurrency({ value, currency, notation: 'compact', decimals: 1 })
			)
			.join(' + ');
		return (
			<Tooltip title={`Mixed currencies: ${breakdown}`} arrow>
				<Box component="span">{compact} *</Box>
			</Tooltip>
		);
	};

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconSpendPlan}
				title={t('spendPlan.spendPlanning')}
					subtitle={t('spendPlan.spendPlanning.subtitle')}
				variant="financial"
				secondaryBadge={<CustomFieldsLink entity="spendPlan" />}
				actions={
					<BtnSplit
						actions={[
							{
								children: t('spendPlan.createSpendPlan'),
								onClick: handleOpenCreateDialog,
								startIcon: <IconAdd />,
							},
							{
								children: t('spendPlan.bulkUploadFromFile'),
								onClick: handleBulkUploadFile,
								startIcon: <IconUpload />,
							},
							{
								children: t('spendPlan.downloadTemplate'),
								onClick: handleDownloadTemplate,
								startIcon: <IconDownload />,
							},
						]}
					/>
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Summary Cards */}
				<StatCardRow
					loading={isLoading}
					cards={[
						{
							icon: IconSpendPlan,
							label: t('spendPlan.totalPlans'),
							value: summaryStats.totalCount,
							secondary: t('spendPlan.approvedCount', { count: summaryStats.approvedCount }),
							color: COLORS.primary.main,
						},
						{
							icon: IconAccount,
							label: t('spendPlan.totalEarmarked'),
							value: renderCurrencyTotal(summaryStats.earmarkedByCurrency),
							color: COLORS.success.main,
						},
						{
							icon: IconCalendar,
							label: t('spendPlan.totalPlanned'),
							value: renderCurrencyTotal(summaryStats.plannedByCurrency),
							valueLocal: summaryStats.plannedLocal,
							color: COLORS.info.main,
						},
					]}
				/>
				{/* DataTable with integrated search, filters, and pagination */}
				<SpendPlansDataTable
					spendPlans={filteredSpendPlans}
					isLoading={isLoading}
					onEditSpendPlan={handleOpenEditDialog}
					onViewSpendPlan={handleViewSpendPlan}
					onDeleteSpendPlan={handleDeleteSpendPlan}
					onSortChange={handleSortChange}
					order={filterOrder}
					orderBy={filterOrderBy}
					page={pagination.page}
					rowsPerPage={pagination.perPage}
					totalCount={pagination.total}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					searchValue={searchQuery}
					onSearchChange={handleSearchChange}
					filterValues={{
						account: filterAccount ? parseInt(filterAccount) : 'ALL',
						status: filterStatus,
					}}
					onFilterChange={handleFilterChange}
				/>
				{/* Help text */}
				<Paper
					elevation={0}
					sx={{
						bgcolor: COLORS.info.light,
						border: '1px solid',
						borderColor: alpha(COLORS.info.main, 0.2),
						borderRadius: 3,
						mt: 3,
						p: 2.5,
					}}>
					<Stack direction="row" alignItems="flex-start" spacing={2}>
						<Avatar
							sx={{
								bgcolor: COLORS.info.main,
								height: 32,
								width: 32,
							}}>
							<IconAccount sx={{ fontSize: 18 }} />
						</Avatar>
						<Box>
							<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
								{t('spendPlan.howSpendPlanningWorks')}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('spendPlan.howSpendPlanningWorksDescription')}
							</Typography>
						</Box>
					</Stack>
				</Paper>{' '}
			</Box>

			{/* Bulk Upload Spend Plans Dialog */}
			<DialogBulkUpload
				isOpen={paramDialog === 'bulkUpload'}
				onClose={handleCloseDialog}
				schema={v2SpendPlanSchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				customFieldDefinitions={project?.customFieldDefinitions}
				onSuccess={() => {
					refetchAccounts();
					setBanner({ message: t('spendPlan.spendPlanUpdatedSuccessfully'), severity: 'success' });
				}}
			/>

			{/* Delete Draft Spend Plan Confirmation */}
			<DialogActions
				isOpen={spendPlanPendingHardDelete !== null}
				onClose={() => !isHardDeletingSpendPlan && setSpendPlanPendingHardDelete(null)}
				maxWidth={520}
				title={{ children: t('spendPlan.deleteDialogTitle') }}
				subtitle={{
					children: t('spendPlan.activities.deleteDialogSubtitle', { name: spendPlanPendingHardDelete?.name ?? '' }),
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						disabled: isHardDeletingSpendPlan,
						onClick: () => setSpendPlanPendingHardDelete(null),
						variant: 'text',
					},
					{
						children: isHardDeletingSpendPlan ? t('spendPlan.activities.deleting') : t('spendPlan.deleteSpendPlan'),
						color: 'error',
						disabled: isHardDeletingSpendPlan,
						loading: isHardDeletingSpendPlan,
						onClick: handleConfirmHardDeleteSpendPlan,
						variant: 'contained',
					},
				]}>
				<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
					{t('spendPlan.deleteDialogBody')}
				</Typography>
			</DialogActions>
		</Box>
	);
};
